import GEO_PRA from "../geo/data_exemple_PRA.json";
import GEO_RA from "../geo/data_exemple_RA.json";
import GEO_DEPT from "../geo/data_exemple_DEPT.json";
import GEO_REG from "../geo/data_exemple_REG.json";
import { CULTURES, semisPourCultureLabel, STADES_RECO } from "../constants.js";
import { libelleHorizon } from "../utils/horizon.js";
// ============================================================
//  DONNÉES DE DÉMONSTRATION (mock) — à utiliser tant que l'API
//  RPlumber de Clément n'est pas prête. Le format renvoyé ici est
//  volontairement identique à celui attendu de la vraie API (voir
//  le contrat dans le README) pour ne rien avoir à changer dans les
//  composants le jour où on bascule sur les vraies routes.
// ============================================================



const YEARS = [2020, 2035, 2050];
const RECHAUFFEMENT = ["reference", "+2", "+2.7"];

/**
 * PRA de l'exploitation de démo — réutilisée par toutes les cartes mock
 * (Impacts globaux, Risque actuel, Phénologie, Fréquence aléas, Eau) pour
 * savoir laquelle souligner (uniquement à l'échelle PRA).
 */
const ID_PRA_EXPLOITATION = "91337";

/**
 * Localisation du siège social de l'exploitation de démo (mock de
 * getGeoLocaExploitation), réutilisée par les cartes Phénologie/Fréquence
 * aléas/Eau — mêmes coordonnées que Risque actuel, centre approximatif de la
 * PRA 91337.
 */
const POINT_EXPLOITATION = { lat: 48.592, lng: 2.465 };

const SOURCE_PAR_ECHELLE = { pra: GEO_PRA, ra: GEO_RA, departement: GEO_DEPT, region: GEO_REG };
const IDPROP_PAR_ECHELLE = {
  pra: "idPetiteRegionAgricole",
  ra: "idRegionAgricole",
  departement: "departement",
  region: "idRegion",
};

/**
 * Construit un FeatureCollection GEO_<échelle> avec une propriété `champ`
 * calculée pour chaque zone à partir d'un indice de sévérité générique
 * (son propre `risques7`/10, présent dans les 4 fichiers geo livrés avec le
 * projet), en appliquant `formule`. Les contours viennent des vrais
 * fichiers ; seule la valeur est simulée.
 */
function construireCartePRA(champ, formule, echelle = "pra") {
  const source = SOURCE_PAR_ECHELLE[echelle] ?? GEO_PRA;
  const src = source.data ?? source;
  const idProp = IDPROP_PAR_ECHELLE[echelle] ?? "idPetiteRegionAgricole";

  // risques7 brut n'a pas la même échelle selon le niveau géographique (un
  // département agrège beaucoup plus de risque qu'une PRA) : sans ça, les
  // cartes Département/Région débordaient presque toutes au-dessus du
  // palier le plus haut de la palette, d'où l'impression de n'avoir que 2-3
  // couleurs visibles. On renormalise chaque échelle sur la même plage
  // 10-25 (la plage naturelle des PRA) avant d'appliquer la formule.
  const brutes = src.features.map((f) => Number(f.properties.risques7));
  const bMin = Math.min(...brutes);
  const bMax = Math.max(...brutes);
  const normaliser = (v) =>
    bMax === bMin ? 18 : 10 + ((v - bMin) / (bMax - bMin)) * 15;

  return {
    idPraExploitation: echelle === "pra" ? ID_PRA_EXPLOITATION : undefined,
    data: {
      type: "FeatureCollection",
      features: src.features.map((feature) => {
        const base = normaliser(Number(feature.properties.risques7));
        const id = feature.properties[idProp];
        return {
          ...feature,
          properties: {
            ...feature.properties,
            [champ]: Number(formule(base, id).toFixed(1)),
          },
        };
      }),
    },
  };
}

function addDays(dateStr, n) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function seedFrom(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
}

function rand(seed) {
  // petit générateur pseudo-aléatoire déterministe (évite que les graphes
  // sautent dans tous les sens à chaque re-render)
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// "04.15" -> "2025-04-15"
function semisToDate(semis) {
  return `2025-${(semis || "04.15").replace(".", "-")}`;
}

/**
 * Facteur "bidon" par scénario climatique (RCP_OPTIONS), pour que les mocks
 * varient VRAIMENT par scénario et pas seulement par année : sans ça, changer
 * le scénario dans la sidebar ne changeait rien à l'affichage tant que le
 * flag USE_MOCK correspondant était à true. Valeurs arbitraires mais
 * cohérentes : 8.5 et TRACC plus sévères que 4.5, hybride entre les deux.
 */
function facteurRcp(rcp) {
  switch (rcp) {
    case "8.5": return 1.3;
    case "hybride": return 1.15;
    case "tracc": return 1.5;
    case "4.5":
    default: return 1;
  }
}

const arrondi1 = (v) => Number(v.toFixed(1));
const arrondi2 = (v) => Number(v.toFixed(2));

export const mock = {
  /* ─────────────────────────────────────────────────────────────────────────
     IMPACTS GLOBAUX — carte du risque moyen de la PRA de l'exploitation et de
     ses voisines. Même forme que les cartes de risques actuels : une entrée
     par zone, avec son code et sa valeur.
     `exploitation: true` marque la PRA de l'utilisateur.
     ───────────────────────────────────────────────────────────────────────── */
  getCarteRisquePRA({ annee = 2035 } = {}) {
    // On réutilise les contours réels livrés avec le projet
    // (shared/geo/data_exemple_PRA.json), exactement le format renvoyé par
    // l'API des cartes de risques actuels : { data: FeatureCollection }.
    // Seule la valeur affichée est simulée, les géométries sont authentiques.
    const facteur = annee === 2050 ? 1.35 : annee === 2020 ? 0.7 : 1;
    return Promise.resolve(
      construireCartePRA("risqueMoyen", (base) => base * facteur)
    );
  },

  /* ─────────────────────────────────────────────────────────────────────────
     COMPARER — Risque actuel : les 3 encarts (Courant/Exceptionnel/
     Catastrophique), le graphique "3 vagues" (position de l'exploitation
     dans la distribution des exploitations comparables) et la carte.
     Les 4 fichiers geo (PRA/RA/DEPT/REG) portent déjà risques7/20/50 pour
     chaque zone : ce sont ces valeurs réelles (fournies avec le projet) qui
     alimentent la carte et les encarts, pas des valeurs recalculées — /10
     pour repasser en %, comme le fait déjà le reste de l'appli avec ce
     fichier. ⚠️ Contrat de la vraie route (page/risquerendement) non
     confirmé pour ce nouveau format (l'ancien gère en plus un cas
     viticulture à part) : la génération du graphique 3 vagues reste
     simulée en attendant.
     ───────────────────────────────────────────────────────────────────────── */
  /* ─────────────────────────────────────────────────────────────────────────
     COMPARER — Risque actuel, graphique "3 vagues".
     Contrat RÉEL confirmé (page/risquerendement), forme PLATE — pas de
     risques/vagues/carte imbriqués comme dans une version précédente :
       { dataVagues7, dataVagues20, dataVagues50, dataPerso, libelleExploitation1 }
     dataPerso = [{x, y:0}, {x, y:80}] : x est le RANG de l'exploitation dans
     la population (constant, indépendant du niveau de risque). Les 3
     encarts (Courant/Exceptionnel/Catastrophique) ne sont PAS renvoyés à
     part : on les lit directement dans dataVagues7/20/50 à ce rang — c'est
     le composant qui fait cette lecture, pas ce mock.
     ───────────────────────────────────────────────────────────────────────── */
  getRisqueActuel({ idExploitation } = {}) {
    const seed = seedFrom(String(idExploitation || "") + "risque-actuel");
    const TAILLE_POPULATION = 530;
    const RANG_EXPLOITATION = 336; // fixe, indépendant du niveau de risque

    function population(depart, arrivee, decalageSeed) {
      const valeurs = [];
      for (let i = 0; i < TAILLE_POPULATION; i += 1) {
        const progression = depart + ((arrivee - depart) * i) / (TAILLE_POPULATION - 1);
        const bruit = (rand(seed + decalageSeed + i) - 0.5) * 4;
        valeurs.push({ x: i + 1, y: Number(Math.max(0, progression + bruit).toFixed(2)) });
      }
      return valeurs;
    }

    return Promise.resolve({
      dataVagues7: population(14, 45, 0),
      dataVagues20: population(19, 65, 1000),
      dataVagues50: population(24, 82, 2000),
      dataPerso: [
        { x: RANG_EXPLOITATION, y: 0 },
        { x: RANG_EXPLOITATION, y: 80 },
      ],
      libelleExploitation1: `Exploitation ${idExploitation ?? ""}`.trim(),
    });
  },

  /* ─────────────────────────────────────────────────────────────────────────
     COMPARER — Risque actuel, la carte. Route séparée de la précédente (le
     vrai payload de page/risquerendement ne contient aucune donnée
     géographique). ⚠️ Contrat réel non confirmé : les 4 fichiers geo (PRA/
     RA/DEPT/REG) livrés avec le projet portent déjà risques7/20/50 par
     zone, réutilisés tels quels (/10) en attendant la vraie route carte +
     la vraie localisation de l'exploitation.
     ───────────────────────────────────────────────────────────────────────── */
  getCarteRisqueActuel({ echelle = "pra" } = {}) {
    const GEO_PAR_ECHELLE = {
      pra: GEO_PRA,
      ra: GEO_RA,
      departement: GEO_DEPT,
      region: GEO_REG,
    };
    const source = GEO_PAR_ECHELLE[echelle] ?? GEO_PAR_ECHELLE.pra;
    const src = source.data ?? source;

    // Les 3 champs (risques7/20/50) arrivent d'un coup, comme le fait la
    // vraie route (getGeoPetiteRegionAgricole etc.) : le niveau de risque
    // choisi dans la sidebar ne change QUE le champ lu côté composant, pas
    // la requête — inutile de refetch quand on change juste de niveau.
    // Les fichiers geo livrés avec le projet portent ces valeurs à une
    // échelle x10 (ex. 487 pour 48.7) : on divise pour rester dans la
    // fourchette 0-100 de la vraie palette. La vraie route, elle, renverra
    // ces champs déjà à la bonne échelle, sans division à faire.
    return Promise.resolve({
      carte: {
        data: {
          type: "FeatureCollection",
          features: src.features.map((feature) => ({
            ...feature,
            properties: {
              ...feature.properties,
              risques7: Number((Number(feature.properties.risques7) / 10).toFixed(1)),
              risques20: Number((Number(feature.properties.risques20) / 10).toFixed(1)),
              risques50: Number((Number(feature.properties.risques50) / 10).toFixed(1)),
            },
          })),
        },
      },
      // Point noir : localisation du siège social (route réelle
      // getGeoLocaExploitation), mocké au centre approximatif de la PRA
      // 91337 en attendant.
      pointExploitation: { lat: 48.592, lng: 2.465 },
    });
  },

  /* ─────────────────────────────────────────────────────────────────────────
     COMPARER — Phénologie : 2 cartes (2035/2050), tons bleus, pour la culture
     choisie dans la sidebar. Valeur affichée : avancement de la maturité par
     rapport à 2020 (jours), à titre indicatif.
     ───────────────────────────────────────────────────────────────────────── */
  getCartesPhenologie({ rcp, echelle = "pra", stade } = {}) {
    const f = facteurRcp(rcp);
    // Le stade choisi ne fait varier que l'intensité (mock) : plus tardif
    // dans la campagne, plus l'avancement climatique cumulé est marqué.
    const g = 1 + (STADES_RECO.findIndex((s) => s.id === stade) + 1) * 0.15;
    // En TRACC, les horizons sont des niveaux de réchauffement (2 et 2.7),
    // pas des années — mêmes clés que partout ailleurs dans l'appli.
    const [h1, h2] = rcp === "tracc" ? ["2", "2.7"] : [2035, 2050];
    return Promise.resolve({
      cartes: {
        [h1]: construireCartePRA("avancementJours", (base) => (base - 10) * 0.35 * f * g, echelle),
        [h2]: construireCartePRA("avancementJours", (base) => (base - 10) * 0.7 * f * g, echelle),
      },
      pointExploitation: POINT_EXPLOITATION,
    });
  },

  /* ─────────────────────────────────────────────────────────────────────────
     COMPARER — Fréquence des aléas (nombres aléas) : 3 cartes (2020/2035/
     2050), teinte selon l'aléa choisi dans la sidebar (appliquée côté
     composant via un dégradé continu, pas ici). Valeur affichée : jours/an avec
     cet aléa.
     ───────────────────────────────────────────────────────────────────────── */
  getCartesFrequenceAleas({ rcp, echelle = "pra" } = {}) {
    const f = facteurRcp(rcp);
    const [h1, h2, h3] = rcp === "tracc" ? ["reference", "2", "2.7"] : [2020, 2035, 2050];
    return Promise.resolve({
      cartes: {
        [h1]: construireCartePRA("joursAlea", (base) => base * 0.55 * f, echelle),
        [h2]: construireCartePRA("joursAlea", (base) => base * 0.8 * f, echelle),
        [h3]: construireCartePRA("joursAlea", (base) => base * 1.15 * f, echelle),
      },
      pointExploitation: POINT_EXPLOITATION,
    });
  },

  /* ─────────────────────────────────────────────────────────────────────────
     COMPARER — Eau : 2 cartes (2035/2050), dégradé rouge (besoin en hausse)
     / bleu (besoin en baisse) selon la production choisie dans la sidebar.
     ───────────────────────────────────────────────────────────────────────── */
  getCartesEau({ rcp, echelle = "pra" } = {}) {
    const f = facteurRcp(rcp);
    // Signe déterministe à partir de l'id de zone (pas juste "77058" en dur,
    // qui n'existe qu'à l'échelle PRA) : varie un peu selon les zones quelle
    // que soit l'échelle affichée, pour garder du bleu ET du rouge visibles.
    const signe = (id) => {
      const somme = String(id).split("").reduce((s, c) => s + c.charCodeAt(0), 0);
      return somme % 3 === 0 ? -1 : 1;
    };
    const [h1, h2] = rcp === "tracc" ? ["2", "2.7"] : [2035, 2050];
    return Promise.resolve({
      cartes: {
        [h1]: construireCartePRA("ecartBesoin", (base, id) => (base - 18) * 0.9 * f * signe(id), echelle),
        [h2]: construireCartePRA("ecartBesoin", (base, id) => (base - 18) * 1.6 * f * signe(id), echelle),
      },
      pointExploitation: POINT_EXPLOITATION,
    });
  },

  /* ─────────────────────────────────────────────────────────────────────────
     IMPACTS GLOBAUX — page/optimisation
     Valeurs 2degre/4degre reprises telles quelles d'une réponse réelle de
     l'API. Hybride et TRACC n'existent pas encore côté serveur : ils sont
     extrapolés à partir des deux premiers.
       - hybride : trajectoire RCP 4.5 jusqu'en 2035 puis bascule vers 8.5,
         donc interpolation progressive de 2degre vers 4degre.
       - tracc   : raisonne en réchauffement et non en année. La référence
         reprend 2020, +2 °C correspond à l'horizon 2035 et +2.7 °C se situe
         entre 2035 et 2050 (≈70 % du chemin).
     ───────────────────────────────────────────────────────────────────────── */
  getImpactsGlobaux() {
    const base = {
      // L'assolement arrive dans la même réponse que les risques.
      dataSurface: [
        { name: "Blé tendre : 20ha\n (10.25)", value: 20, itemStyle: { color: "#FFD705" } },
        { name: "Blé dur : 9.96ha\n (10.25)", value: 9.96, itemStyle: { color: "#F8BE3E" } },
        { name: "Maïs grain : 10ha\n (04.30)", value: 10, itemStyle: { color: "#92D050" } },
      ],
      dataPB: [
        { name: "Blé tendre : 21625€\n (10.25)", value: 21625, itemStyle: { color: "#FFD705" } },
        { name: "Blé dur : 12373.91€\n (10.25)", value: 12373.9056, itemStyle: { color: "#F8BE3E" } },
        { name: "Maïs grain : 15745.8€\n (04.30)", value: 15745.8, itemStyle: { color: "#92D050" } },
      ],
      PBTot: 49744.7056,
      SurfaceTot: 39.96,
      // Affichés sous le titre de chaque tableau.
      commune: "67260 - Sarre-Union",
      libellePRA: "PLATEAU LORRAIN NORD",
      // Série "sans assurance" comparée sur GraphDifference (1er point vide,
      // aligné sur les 4 niveaux de risque affichés hors "Très courant").
      dataGraph2020SimFerme: [undefined, 22.4, 34.1, 47.8],
      R2020_2degre: [19.0493, 28.9514, 36.5145, 43.0887, 62.6277],
      R2025_2degre: [20.9108, 29.5639, 35.6692, 40.2732, 66.5004],
      R2030_2degre: [19.2149, 27.9507, 35.9907, 68.6689, 77.2618],
      R2035_2degre: [21.4791, 31.9722, 42.7648, 64.4437, 75.8141],
      R2040_2degre: [23.2876, 31.3388, 40.7172, 46.7667, 62.7329],
      R2045_2degre: [21.2137, 28.3046, 36.0299, 77.6021, 87.2949],
      R2050_2degre: [23.9319, 34.7609, 51.6388, 79.0166, 87.3251],
      RMoy_2degre: [15.4823, 15.8944, 15.7391, 18.0916, 18.4942, 17.7549, 20.0626],
      R2020_4degre: [19.0493, 28.9514, 36.5145, 43.0887, 62.6277],
      R2025_4degre: [23.8809, 32.7386, 43.0089, 70.3534, 78.4637],
      R2030_4degre: [24.7412, 36.277, 52.4373, 75.6229, 83.3551],
      R2035_4degre: [25.3613, 34.9384, 44.3577, 55.1649, 72.7551],
      R2040_4degre: [25.9632, 34.1436, 41.6522, 47.629, 55.17],
      R2045_4degre: [26.8699, 35.5689, 45.0447, 53.2393, 67.9446],
      R2050_4degre: [24.0684, 33.5303, 43.5327, 50.5975, 68.3933],
      RMoy_4degre: [15.4823, 20.3281, 20.8694, 20.0214, 20.5243, 21.5453, 20.0068],
    };

    const ANNEES = [2020, 2025, 2030, 2035, 2040, 2045, 2050];
    const melange = (a, b, part) =>
      a.map((v, i) => Number((v + (b[i] - v) * part).toFixed(4)));

    // Poids de bascule 4.5 -> 8.5 : nul en 2020, total en 2050.
    const partHybride = (annee) => Math.max(0, (annee - 2020) / 30);

    const resultat = { ...base, idExploitation: 173619 };

    ANNEES.forEach((annee) => {
      resultat[`R${annee}_hybride`] = melange(
        base[`R${annee}_2degre`],
        base[`R${annee}_4degre`],
        partHybride(annee)
      );
    });
    resultat.RMoy_hybride = base.RMoy_2degre.map((v, i) =>
      Number((v + (base.RMoy_4degre[i] - v) * partHybride(ANNEES[i])).toFixed(4))
    );

    resultat.Rreference_tracc = base.R2020_2degre;
    resultat.R2_tracc = base.R2035_2degre;
    resultat["R2.7_tracc"] = melange(base.R2035_2degre, base.R2050_2degre, 0.7);
    resultat.RMoy_tracc = [
      base.RMoy_2degre[0],
      base.RMoy_2degre[3],
      Number((base.RMoy_2degre[3] + (base.RMoy_2degre[6] - base.RMoy_2degre[3]) * 0.7).toFixed(4)),
    ];

    return Promise.resolve(resultat);
  },

  /* ─────────────────────────────────────────────────────────────────────────
     ADAPTATIONS — trois nouvelles routes, valeurs plausibles en attendant.
     ───────────────────────────────────────────────────────────────────────── */

  /**
   * Classement des solutions d'adaptation par gain de risque évité.
   *
   * La réponse contient directement une liste par horizon (le choix de
   * l'année se fait côté page, sans nouvel appel) :
   *   RCP   : { 2020: [...], 2035: [...], 2050: [...] }
   *   TRACC : { reference: [...], 2: [...], "2.7": [...] }
   */
  getClassementSolutions({ rcp } = {}) {
    // impact   : écart de risque entre l'horizon et la référence, en points de
    //            produit brut (positif = risque évité).
    // ecartType: dispersion du résultat entre scénarios climatiques. Faible =
    //            solution "sans regret", élevée = "dépend des scénarios".
    // Valeurs saisies à la main, à écraser par les vraies données ;
    // facteurRcp ne sert qu'à différencier les scénarios.
    const SOLUTIONS_PAR_ANNEE = {
      2020: [
        { solution: "Semis Maïs", impact: 2.6, ecartType: 0.5 },
        { solution: "Semis Tournesol", impact: 2.2, ecartType: 0.6 },
        { solution: "Variété Maïs", impact: 3.3, ecartType: 0.7 },
        { solution: "Substitution Maïs -> Blé tendre hiver", impact: 1.8, ecartType: 1.5 },
        { solution: "Substitution Colza-> Tournesol", impact: 2.1, ecartType: 1.3 },
        { solution: "Substitution Blé tendre hiver -> Soja précocité 0", impact: 2.4, ecartType: 1.8 },
        { solution: "Irrigation Maïs", impact: 4.0, ecartType: 2.0 },
        { solution: "Irrigation Tournesol", impact: 3.0, ecartType: 1.7 },
      ],
      2035: [
        { solution: "Semis Maïs", impact: 4.6, ecartType: 0.8 },
        { solution: "Semis Tournesol", impact: 3.9, ecartType: 1.0 },
        { solution: "Variété Maïs", impact: 5.8, ecartType: 1.1 },
        { solution: "Substitution Maïs -> Blé tendre hiver", impact: 3.1, ecartType: 2.6 },
        { solution: "Substitution Colza-> Tournesol", impact: 3.7, ecartType: 2.2 },
        { solution: "Substitution Blé tendre hiver -> Soja précocité 0", impact: 4.2, ecartType: 3.1 },
        { solution: "Irrigation Maïs", impact: 6.9, ecartType: 3.4 },
        { solution: "Irrigation Tournesol", impact: 5.2, ecartType: 2.9 },
      ],
      2050: [
        { solution: "Semis Maïs", impact: 6.7, ecartType: 1.2 },
        { solution: "Semis Tournesol", impact: 5.7, ecartType: 1.5 },
        { solution: "Variété Maïs", impact: 8.4, ecartType: 1.6 },
        { solution: "Substitution Maïs -> Blé tendre hiver", impact: 4.5, ecartType: 3.8 },
        { solution: "Substitution Colza-> Tournesol", impact: 5.4, ecartType: 3.2 },
        { solution: "Substitution Blé tendre hiver -> Soja précocité 0", impact: 6.1, ecartType: 4.5 },
        { solution: "Irrigation Maïs", impact: 10.0, ecartType: 4.9 },
        { solution: "Irrigation Tournesol", impact: 7.5, ecartType: 4.2 },
      ],
    };
    // En TRACC les horizons sont reference / 2 / 2.7 : mêmes trois listes.
    const CLE_TRACC = { 2020: "reference", 2035: "2", 2050: "2.7" };
    const facteur = facteurRcp(rcp);

    const reponse = {};
    Object.entries(SOLUTIONS_PAR_ANNEE).forEach(([annee, solutions]) => {
      const cle = rcp === "tracc" ? CLE_TRACC[annee] : annee;
      reponse[cle] = solutions.map((s) => ({
        ...s,
        impact: arrondi1(s.impact * facteur),
        ecartType: arrondi2(s.ecartType * facteur),
      }));
    });
    return Promise.resolve(reponse);
  },

  /**
   * Effet d'un changement de date de semis ou de variété.
   *
   * - Semis : dates réelles de la culture choisie (idProduction porte alors
   *   son libellé, cf. ChangementSemisVariete.jsx), depuis SEMIS_PAR_CULTURE.
   * - Variété : seuls maïs et soja sont simulés ; leurs échelles de précocité
   *   ne sont pas les mêmes (5 stades pour le maïs, 3 précocités pour le
   *   soja), déterminées ici depuis la famille de l'idProduction numérique.
   */
  getChangementSemisVariete({ type = "semis", rcp, idProduction } = {}) {
    let modalites;
    if (type === "semis") {
      modalites = semisPourCultureLabel(idProduction).map((s) => s.label);
    } else {
      const famille = CULTURES.find((c) => c.id === idProduction)?.famille;
      modalites =
        famille === "soja"
          ? ["Précocité 000", "Précocité 00", "Précocité 0"]
          : ["Très précoce", "Précoce", "Demi-précoce", "Demi-tardif", "Tardif"];
    }

    // Les horizons suivent le scénario climatique choisi, comme partout
    // ailleurs : années pour les RCP classiques, niveaux de réchauffement en
    // TRACC (c'était figé sur 2020/2035/2050 même en TRACC auparavant).
    const horizons =
      rcp === "tracc"
        ? ["reference", "2", "2.7"].map((h) => libelleHorizon("tracc", h))
        : [2020, 2035, 2050];

    const f = facteurRcp(rcp);
    // Les modalités les plus tardives se dégradent plus vite avec l'horizon.
    return Promise.resolve({
      horizons,
      series: modalites.map((nom, rang) => {
        const depart = 9.5 + rang * 0.6;
        const pente = 1.8 + rang * 0.9;
        return {
          nom,
          valeurs: [0, 1, 2].map((k) => arrondi1((depart + pente * k) * f)),
        };
      }),
    });
  },

  /** Comparaison de la culture actuelle et des cultures de substitution. */
  getSubstitution({ annee = 2035, rcp, cultures = [] } = {}) {
    // ecartPB     : PB de la culture actuelle - PB de la remplaçante (€).
    // ecartRisque : risque actuel - risque de la remplaçante (points).
    // Positif en Y = la substitution réduit le risque.
    // En TRACC les horizons sont reference / 2 / 2.7 (= 2020 / 2035 / 2050).
    const horizon = { reference: 2020, "2": 2035, "2.7": 2050 }[String(annee)] ?? Number(annee);
    const facteur = (horizon === 2050 ? 1.4 : horizon === 2020 ? 0.6 : 1) * facteurRcp(rcp);
    const liste = cultures.length ? cultures : [
      "Blé tendre hiver", "Orge hiver", "Maïs", "Tournesol", "Colza",
      "Pois hiver", "Soja 00", "Lentille", "Betterave",
    ];
    return Promise.resolve({
      cultures: liste.map((culture, rang) => {
        // Valeurs déterministes, réparties dans les quatre cadrans.
        const angle = (rang / Math.max(1, liste.length)) * 2 * Math.PI;
        return {
          culture,
          ecartPB: Math.round(Math.cos(angle) * 4200 + 600),
          ecartRisque: arrondi1(Math.sin(angle) * 5.4 * facteur + 1.2),
        };
      }),
    });
  },

  /* ─────────────────────────────────────────────────────────────────────────
     EAU ET IRRIGATION / TENSION — route page/tension.
     Contrat : { idExploitation, rcp } -> { elements: [...] }
     Plus de paramètre `annee` : l'API renvoie TOUS les horizons d'un coup,
     un élément par (cle, horizon), et c'est le front qui bascule d'un
     horizon à l'autre en filtrant sur `Annee` — changer d'année dans la
     sidebar ne relance donc aucun appel.
       { cle, label, type, Annee,
         points: [{ date, eau_moy, eau_med, eau_q75, eau_q90, eau_max }] }
     `cle` : "mais_grain" / "tournesol" / "cochons" / "vaches_laitieres".
     `Annee` : 2020/2035/2050, ou "reference"/"+2"/"+2.7" en TRACC.
     ───────────────────────────────────────────────────────────────────────── */
  /**
   * Une fonction par élément (lignesTensionMais, lignesTensionTournesol,
   * lignesTensionVaches, lignesTensionCochons, juste après le mock) produit
   * la moyenne journalière par horizon ; les autres statistiques (médiane,
   * Q75, Q90, max) en sont déduites ici, croissantes. Pour brancher de
   * vraies valeurs, remplacer le corps de ces fonctions.
   */
  getTension({ rcp = "4.5" } = {}) {
    const DEFINITIONS = [
      { cle: "mais_grain", label: "Maïs grain", type: "culture", lignes: lignesTensionMais },
      { cle: "tournesol", label: "Tournesol", type: "culture", lignes: lignesTensionTournesol },
      { cle: "cochons", label: "Cochons", type: "elevage", lignes: lignesTensionCochons },
      { cle: "vaches_laitieres", label: "Vaches laitières", type: "elevage", lignes: lignesTensionVaches },
    ];
    const annees = rcp === "tracc" ? ["reference", "+2", "+2.7"] : YEARS;
    // Les générateurs numérotent les horizons TRACC "2"/"2.7" (sans "+").
    const cleGenerateur = (annee) => (rcp === "tracc" && annee !== "reference" ? annee.replace("+", "") : annee);

    const elements = [];
    DEFINITIONS.forEach(({ cle, label, type, lignes }) => {
      const toutes = lignes(rcp);
      annees.forEach((annee) => {
        const points = toutes
          .filter((l) => String(l.Years) === String(cleGenerateur(annee)))
          .map((l, i) => {
            const moy = l.besoin_moy;
            const r = (k) => rand(seedFrom(`${cle}-${annee}`) + i * 7 + k);
            return {
              date: l.date_affichage,
              eau_moy: Number(moy.toFixed(4)),
              eau_med: Number((moy * (0.96 + 0.02 * r(1))).toFixed(4)),
              eau_q75: Number((moy * (1.12 + 0.04 * r(2))).toFixed(4)),
              eau_q90: Number((moy * (1.26 + 0.05 * r(3))).toFixed(4)),
              eau_max: Number((moy * (1.46 + 0.08 * r(4))).toFixed(4)),
            };
          });
        elements.push({ cle, label, type, Annee: annee, points });
      });
    });
    return Promise.resolve({ elements });
  },

  /* ─────────────────────────────────────────────────────────────────────────
     TABLEAU DE BORD — effectifs du cheptel (image 492 de la maquette)
     Nouvelle route à créer côté API : page/elevage.
     Contrat proposé : { idExploitation } -> { categories: [{ categorie, effectif }] }
     Valeurs calées sur la maquette pour que le rendu soit représentatif.
     Une catégorie à 0 n'est pas tracée (filtrée par ElevageChart).
     ───────────────────────────────────────────────────────────────────────── */
  getElevage() {
    return Promise.resolve({
      categories: [
        { categorie: "Vaches en lactation, alim maïs ensillage", effectif: 0 },
        { categorie: "Vaches taries & génisses > 2 ans", effectif: 0 },
        { categorie: "Génisses laitières 1 à 2 ans", effectif: 0 },
        { categorie: "Génisses laitières 6 mois à 1 an", effectif: 0 },
        { categorie: "Porc à l'engraissement, alim sec, abreuv bol", effectif: 0 },
        { categorie: "Truie en lactation", effectif: 0 },
        { categorie: "Porc post-sevrage", effectif: 0 },
        { categorie: "Porc nurserie", effectif: 0 },
      ],
    });
  },


/*   
██████╗░██╗░░██╗███████╗███╗░░██╗░█████╗░
██╔══██╗██║░░██║██╔════╝████╗░██║██╔══██╗
██████╔╝███████║█████╗░░██╔██╗██║██║░░██║
██╔═══╝░██╔══██║██╔══╝░░██║╚████║██║░░██║
██║░░░░░██║░░██║███████╗██║░╚███║╚█████╔╝
╚═╝░░░░░╚═╝░░╚═╝╚══════╝╚═╝░░╚══╝░╚════╝░ */

   getPheno({ idProdu, gridno }) {
    const seed = seedFrom(String(idProdu || "") + String(gridno || "") + "pheno");

    // 11 stades = 10 étapes (transitions), calqués sur la phénologie maïs réelle.
    const stades = [
      { BBCH: 0, Libelle_SP: "Semis" },
      { BBCH: 9, Libelle_SP: "Levée" },
      { BBCH: 14, Libelle_SP: "4feuilles" },
      // is_reco : stades affichés avec leur libellé sur le graphique et
      // repris dans le tableau à droite — ce n'était renseigné nulle part
      // avant (bug préexistant, pas lié à la refonte), donc le tableau était
      // toujours vide et aucun libellé n'apparaissait jamais sur la courbe.
      { BBCH: 18, Libelle_SP: "8feuilles", is_reco: true },
      { BBCH: 19, Libelle_SP: "9 ou plus feuilles" },
      { BBCH: 65, Libelle_SP: "Floraison femelle", is_reco: true },
      { BBCH: 69, Libelle_SP: "Limite avortement" },
      { BBCH: 75, Libelle_SP: "50% d'humidité - debut grain pateux" },
      { BBCH: 83, Libelle_SP: "45% d'humidité - grain pateux" },
      { BBCH: 87, Libelle_SP: "38% d'humidité - maturite complete" },
      { BBCH: 89, Libelle_SP: "32% d'humidité", is_reco: true },
    ];

    // Durées (en jours) entre stades consécutifs, par horizon — reprend les
    // valeurs observées sur le graphique de référence. Les stades arrivent
    // un peu plus tôt et un peu plus vite avec le réchauffement (2050 < 2020).
    const DUREES_PAR_HORIZON = {
      2020: [5, 26, 18, 15, 22, 16, 21, 2, 8, 1],
      2035: [5, 25, 17, 14, 22, 15, 21, 2, 7, 1],
      2050: [4, 23, 17, 13, 21, 14, 20, 2, 7, 1],
    };

    // Date de semis par horizon (léger décalage climatique + variation
    // seedée selon la région/culture pour ne pas avoir des courbes identiques
    // partout).
    const SEMIS_PAR_HORIZON = {
      2020: "2023-04-15",
      2035: "2023-04-15",
      2050: "2023-04-15",
    };
    const rows = [];
    YEARS.forEach((YearGroup) => {
      const jitter = Math.round((rand(seed + YearGroup) - 0.5) * 4); // ±2 j
      const semisJour = addDays(SEMIS_PAR_HORIZON[YearGroup], jitter);
      const durees = DUREES_PAR_HORIZON[YearGroup];
 
      let offset = 0;
      let gdd = 30;
      stades.forEach((s, i) => {
        if (i > 0) {
          const dureeJitter = Math.max(1, durees[i - 1] + Math.round((rand(seed + i * 7 + YearGroup) - 0.5) * 2));
          offset += dureeJitter;
          gdd += dureeJitter * (14 + rand(seed + i) * 6); // ~14-20 GDD/jour
        }
        rows.push({
          YearGroup,
          BBCH: s.BBCH,
          JourJulien: offset,
          date_calendaire: addDays(semisJour, offset),
          GDD_VF: Math.round(gdd),
          Libelle_SP: s.Libelle_SP,
          is_reco: s.is_reco ?? false, // fourni par l'API
        });
      });
    });
    return Promise.resolve(rows);
  },

  getPhenoTRACC({ idProdu, gridno }) {
    const seed = seedFrom(String(idProdu || "") + String(gridno || "") + "pheno");

    // 11 stades = 10 étapes (transitions), calqués sur la phénologie maïs réelle.
    const stades = [
      { BBCH: 0, Libelle_SP: "Semis" },
      { BBCH: 9, Libelle_SP: "Levée" },
      { BBCH: 14, Libelle_SP: "4feuilles" },
      // is_reco : stades affichés avec leur libellé sur le graphique et
      // repris dans le tableau à droite — ce n'était renseigné nulle part
      // avant (bug préexistant, pas lié à la refonte), donc le tableau était
      // toujours vide et aucun libellé n'apparaissait jamais sur la courbe.
      { BBCH: 18, Libelle_SP: "8feuilles", is_reco: true },
      { BBCH: 19, Libelle_SP: "9 ou plus feuilles" },
      { BBCH: 65, Libelle_SP: "Floraison femelle", is_reco: true },
      { BBCH: 69, Libelle_SP: "Limite avortement" },
      { BBCH: 75, Libelle_SP: "50% d'humidité - debut grain pateux" },
      { BBCH: 83, Libelle_SP: "45% d'humidité - grain pateux" },
      { BBCH: 87, Libelle_SP: "38% d'humidité - maturite complete" },
      { BBCH: 89, Libelle_SP: "32% d'humidité", is_reco: true },
    ];

    // Durées (en jours) entre stades consécutifs, par horizon — reprend les
    // valeurs observées sur le graphique de référence. Les stades arrivent
    // un peu plus tôt et un peu plus vite avec le réchauffement (2050 < 2020).
    const DUREES_PAR_HORIZON = {
      "reference": [5, 26, 18, 15, 22, 16, 21, 2, 8, 1],
      "+2": [5, 25, 17, 14, 22, 15, 21, 2, 7, 1],
      "+2.7": [4, 23, 17, 13, 21, 14, 20, 2, 7, 1],
    };

    // Date de semis par horizon (léger décalage climatique + variation
    // seedée selon la région/culture pour ne pas avoir des courbes identiques
    // partout).
    const SEMIS_PAR_HORIZON = {
      "reference": "2023-04-15",
      "+2": "2023-04-15",
      "+2.7": "2023-04-15",
    };
    const rows = [];
    RECHAUFFEMENT.forEach((YearGroup, idx) => {
      const jitter = Math.round((rand(seed + idx) - 0.5) * 4); // ±2 j
      const semisJour = addDays(SEMIS_PAR_HORIZON[YearGroup], jitter);
      const durees = DUREES_PAR_HORIZON[YearGroup];

      let offset = 0;
      let gdd = 30;
      stades.forEach((s, i) => {
        if (i > 0) {
          const dureeJitter = Math.max(1, durees[i - 1] + Math.round((rand(seed + i * 7 + idx) - 0.5) * 2));
          offset += dureeJitter;
          gdd += dureeJitter * (14 + rand(seed + i) * 6); // ~14-20 GDD/jour
        }
        rows.push({
          YearGroup,
          BBCH: s.BBCH,
          JourJulien: offset,
          date_calendaire: addDays(semisJour, offset),
          GDD_VF: Math.round(gdd),
          Libelle_SP: s.Libelle_SP,
          is_reco: s.is_reco ?? false, // fourni par l'API
        });
      });
    });
    return Promise.resolve(rows);
  },


/* 
  
░█████╗░██╗░░░░░███████╗░█████╗░░██████╗
██╔══██╗██║░░░░░██╔════╝██╔══██╗██╔════╝
███████║██║░░░░░█████╗░░███████║╚█████╗░
██╔══██║██║░░░░░██╔══╝░░██╔══██║░╚═══██╗
██║░░██║███████╗███████╗██║░░██║██████╔╝
╚═╝░░╚═╝╚══════╝╚══════╝╚═╝░░╚═╝╚═════╝░ */

 getAleas({ idProdu, gridno }) {
    const codes = ["Gel", "CDC", "EE", "SHRU", "CDF"];
    const rows = [];
    codes.forEach((alea, ai) => {
      const seed = seedFrom(String(idProdu || "") + String(gridno || "") + alea);
      YEARS.forEach((YearGroup, yi) => {
        const moy = 4 + yi * 5 + rand(seed + yi + ai * 17) * 3;
        rows.push({ YearGroup, alea, stat: "Min", val: Number((moy * 0.5).toFixed(1)) });
        rows.push({ YearGroup, alea, stat: "Moy", val: Number(moy.toFixed(1)) });
        rows.push({ YearGroup, alea, stat: "Max", val: Number((moy * 1.7).toFixed(1)) });
      });
    });
    return Promise.resolve(rows);
  },

/* 

░█████╗░██╗░░░░░███████╗░█████╗░░██████╗  ████████╗██████╗░░█████╗░░█████╗░░█████╗░
██╔══██╗██║░░░░░██╔════╝██╔══██╗██╔════╝  ╚══██╔══╝██╔══██╗██╔══██╗██╔══██╗██╔══██╗
███████║██║░░░░░█████╗░░███████║╚█████╗░  ░░░██║░░░██████╔╝███████║██║░░╚═╝██║░░╚═╝
██╔══██║██║░░░░░██╔══╝░░██╔══██║░╚═══██╗  ░░░██║░░░██╔══██╗██╔══██║██║░░██╗██║░░██╗
██║░░██║███████╗███████╗██║░░██║██████╔╝  ░░░██║░░░██║░░██║██║░░██║╚█████╔╝╚█████╔╝
╚═╝░░╚═╝╚══════╝╚══════╝╚═╝░░╚═╝╚═════╝░  ░░░╚═╝░░░╚═╝░░╚═╝╚═╝░░╚═╝░╚════╝░░╚════╝░ */
  getAleasTRACC({ idProdu, gridno }) {
    const codes = ["Gel", "CDC", "EE", "SHRU", "CDF"];
    const rows = [];
    codes.forEach((alea, ai) => {
      const seed = seedFrom(String(idProdu || "") + String(gridno || "") + alea);
      RECHAUFFEMENT.forEach((YearGroup, yi) => {
        const moy = 4 + yi * 5 + rand(seed + yi + ai * 17) * 3;
        rows.push({ YearGroup, alea, stat: "Min", val: Number((moy * 0.5).toFixed(1)) });
        rows.push({ YearGroup, alea, stat: "Moy", val: Number(moy.toFixed(1)) });
        rows.push({ YearGroup, alea, stat: "Max", val: Number((moy * 1.7).toFixed(1)) });
      });
    });
    return Promise.resolve(rows);
  },



   getPertes({ rcp } = {}) {
    const f = facteurRcp(rcp);
    const aleasParCultureUneAnnee = [
      { culture: "Blé tendre", Gel: 2.1, CDC: 5.4, SHRU: 6.8, EE: 1.9, CDF: 0.8 },
      { culture: "Blé dur", Gel: 1.8, CDC: 6.1, SHRU: 7.2, EE: 1.4, CDF: 0.6 },
      { culture: "Orge hiver", Gel: 2.6, CDC: 4.2, SHRU: 5.1, EE: 2.2, CDF: 0.9 },
      { culture: "Maïs grain", Gel: 0.4, CDC: 8.9, SHRU: 11.3, EE: 2.8, CDF: 0.2 },
      { culture: "Colza", Gel: 3.4, CDC: 3.8, SHRU: 4.6, EE: 2.5, CDF: 1.4 },
      { culture: "Tournesol", Gel: 1.1, CDC: 7.2, SHRU: 8.4, EE: 1.7, CDF: 0.3 },
    ].map((c) => ({
      culture: c.culture,
      Gel: arrondi1(c.Gel * f),
      CDC: arrondi1(c.CDC * f),
      SHRU: arrondi1(c.SHRU * f),
      EE: arrondi1(c.EE * f),
      CDF: arrondi1(c.CDF * f),
    }));
    // La répartition de l'assolement (surfaces/cultures) ne dépend pas du
    // scénario climatique : pas de facteur ici, contrairement à ci-dessus.
    const repartitionCulturesUneAnnee = [
      { nom: "Blé tendre", valeur: 47.3, couleur: "#FFC000" },
      { nom: "Blé dur", valeur: 5.4, couleur: "#FFFF00" },
      { nom: "Orge hiver", valeur: 2.7, couleur: "#92C47D" },
      { nom: "Maïs grain", valeur: 8.7, couleur: "#843C0B" },
      { nom: "Colza", valeur: 6.5, couleur: "#BDE3F0" },
      { nom: "Tournesol", valeur: 5.4, couleur: "#F4B9A8" },
      { nom: "Pois protéagineux", valeur: 1.1, couleur: "#F6C5A0" },
      { nom: "Prairie", valeur: 22.8, couleur: "#C8A97E" },
    ];

    return Promise.resolve({
      aleasParCulture: {
        "2020": aleasParCultureUneAnnee,
        "2035": aleasParCultureUneAnnee,
        "2050": aleasParCultureUneAnnee,
      },
      repartitionCultures: {
        "2020": repartitionCulturesUneAnnee,
        "2035": repartitionCulturesUneAnnee,
        "2050": repartitionCulturesUneAnnee,
      },
      repartitionAleas: [
        { YearGroup: 2020, alea: "Gel", val: arrondi1(1.8 * f) },
        { YearGroup: 2020, alea: "CDC", val: arrondi1(2.9 * f) },
        { YearGroup: 2020, alea: "EE", val: arrondi1(3.4 * f) },
        { YearGroup: 2020, alea: "SHRU", val: arrondi1(1.1 * f) },
        { YearGroup: 2020, alea: "CDF", val: arrondi1(2.6 * f) },
        { YearGroup: 2035, alea: "Gel", val: arrondi1(3.5 * f) },
        { YearGroup: 2035, alea: "CDC", val: arrondi1(4.2 * f) },
        { YearGroup: 2035, alea: "EE", val: arrondi1(5.0 * f) },
        { YearGroup: 2035, alea: "SHRU", val: arrondi1(2.3 * f) },
        { YearGroup: 2035, alea: "CDF", val: arrondi1(3.9 * f) },
        { YearGroup: 2050, alea: "Gel", val: arrondi1(4.9 * f) },
        { YearGroup: 2050, alea: "CDC", val: arrondi1(5.7 * f) },
        { YearGroup: 2050, alea: "EE", val: arrondi1(6.1 * f) },
        { YearGroup: 2050, alea: "SHRU", val: arrondi1(3.2 * f) },
        { YearGroup: 2050, alea: "CDF", val: arrondi1(5.0 * f) },
      ],
    });
  },

/* 

██╗███╗░░░███╗██████╗░░█████╗░░█████╗░████████╗  ████████╗██████╗░░█████╗░░█████╗░░█████╗░
██║████╗░████║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝  ╚══██╔══╝██╔══██╗██╔══██╗██╔══██╗██╔══██╗
██║██╔████╔██║██████╔╝███████║██║░░╚═╝░░░██║░░░  ░░░██║░░░██████╔╝███████║██║░░╚═╝██║░░╚═╝
██║██║╚██╔╝██║██╔═══╝░██╔══██║██║░░██╗░░░██║░░░  ░░░██║░░░██╔══██╗██╔══██║██║░░██╗██║░░██╗
██║██║░╚═╝░██║██║░░░░░██║░░██║╚█████╔╝░░░██║░░░  ░░░██║░░░██║░░██║██║░░██║╚█████╔╝╚█████╔╝
╚═╝╚═╝░░░░░╚═╝╚═╝░░░░░╚═╝░░╚═╝░╚════╝░░░░╚═╝░░░  ░░░╚═╝░░░╚═╝░░╚═╝╚═╝░░╚═╝░╚════╝░░╚════╝░ */

/**
 * ⚠️ Contrat TRACC non confirmé sur cette route : on suppose les mêmes
 * clés "reference"/"+2"/"+2.7" que partout ailleurs (repartitionAleas,
 * RMoy_tracc...), à vérifier avec Clément dès que l'API répond en TRACC ici.
 */
getPertesTRACC() {
    const aleasParCultureUneAnnee = [
      { culture: "Blé tendre", Gel: 2.7, CDC: 7.0, SHRU: 8.8, EE: 2.5, CDF: 1.0 },
      { culture: "Blé dur", Gel: 2.3, CDC: 7.9, SHRU: 9.4, EE: 1.8, CDF: 0.8 },
      { culture: "Orge hiver", Gel: 3.4, CDC: 5.5, SHRU: 6.6, EE: 2.9, CDF: 1.2 },
      { culture: "Maïs grain", Gel: 0.5, CDC: 11.6, SHRU: 14.7, EE: 3.6, CDF: 0.3 },
      { culture: "Colza", Gel: 4.4, CDC: 4.9, SHRU: 6.0, EE: 3.3, CDF: 1.8 },
      { culture: "Tournesol", Gel: 1.4, CDC: 9.4, SHRU: 10.9, EE: 2.2, CDF: 0.4 },
    ];
    const repartitionCulturesUneAnnee = [
      { nom: "Blé tendre", valeur: 47.3, couleur: "#FFC000" },
      { nom: "Blé dur", valeur: 5.4, couleur: "#FFFF00" },
      { nom: "Orge hiver", valeur: 2.7, couleur: "#92C47D" },
      { nom: "Maïs grain", valeur: 8.7, couleur: "#843C0B" },
      { nom: "Colza", valeur: 6.5, couleur: "#BDE3F0" },
      { nom: "Tournesol", valeur: 5.4, couleur: "#F4B9A8" },
      { nom: "Pois protéagineux", valeur: 1.1, couleur: "#F6C5A0" },
      { nom: "Prairie", valeur: 22.8, couleur: "#C8A97E" },
    ];

    return Promise.resolve({
      aleasParCulture: {
        reference: aleasParCultureUneAnnee,
        "+2": aleasParCultureUneAnnee,
        "+2.7": aleasParCultureUneAnnee,
      },
      repartitionCultures: {
        reference: repartitionCulturesUneAnnee,
        "+2": repartitionCulturesUneAnnee,
        "+2.7": repartitionCulturesUneAnnee,
      },
      repartitionAleas: [
        { YearGroup: "reference", alea: "Gel", val: 2.3 },
        { YearGroup: "reference", alea: "CDC", val: 3.7 },
        { YearGroup: "reference", alea: "EE", val: 4.3 },
        { YearGroup: "reference", alea: "SHRU", val: 1.4 },
        { YearGroup: "reference", alea: "CDF", val: 3.3 },
        { YearGroup: "+2", alea: "Gel", val: 4.4 },
        { YearGroup: "+2", alea: "CDC", val: 5.3 },
        { YearGroup: "+2", alea: "EE", val: 6.3 },
        { YearGroup: "+2", alea: "SHRU", val: 2.9 },
        { YearGroup: "+2", alea: "CDF", val: 4.9 },
        { YearGroup: "+2.7", alea: "Gel", val: 6.2 },
        { YearGroup: "+2.7", alea: "CDC", val: 7.2 },
        { YearGroup: "+2.7", alea: "EE", val: 7.7 },
        { YearGroup: "+2.7", alea: "SHRU", val: 4.0 },
        { YearGroup: "+2.7", alea: "CDF", val: 6.3 },
      ],
    });
  },

/* 

███████╗░█████╗░██╗░░░██╗
██╔════╝██╔══██╗██║░░░██║
█████╗░░███████║██║░░░██║
██╔══╝░░██╔══██║██║░░░██║
███████╗██║░░██║╚██████╔╝
╚══════╝╚═╝░░╚═╝░╚═════╝░ */



  /**
   * Besoin en eau et irrigation, au format de la vraie route "eau" : par
   * enregistrement, 5 statistiques au lieu de moyenne + écart-type —
   *   eau        : Years, jour_campagne, date_affichage,
   *                besoin_moy / _med / _q75 / _q90 / _max
   *   irrigation : Years, jour_campagne, date_affichage,
   *                irr_moy / _med / _q75 / _q90 / _max
   * (la date s'appelle date_affichage dans les deux tableaux.) Les horizons suivent le scénario : 2020/2035/2050, ou
   * reference/+2/+2.7 en TRACC. En élevage, seul `eau` est renvoyé (en
   * L/tête/j, sur l'année civile) ; pour les cultures, la campagne démarre
   * à la date de semis.
   */
  getEauIrrigation({ type, idProduction, semis, rcp } = {}) {
    const elevage = type === "elevage";
    const horizons = rcp === "tracc" ? RECHAUFFEMENT : YEARS;
    // Différencie un peu les scénarios (4.5 < hybride < 8.5 < TRACC).
    const severite = 1 + (facteurRcp(rcp) - 1) * 0.4;
    const seedEau = seedFrom(String(idProduction || "") + "eau");
    const seedIrr = seedFrom(String(idProduction || "") + "irr");

    // À partir d'une moyenne, déduit les 4 autres statistiques (croissantes :
    // médiane <= q75 <= q90 <= max), avec un léger bruit déterministe.
    const statistiques = (prefixe, moy, graine) => {
      const r = (k) => rand(graine + k);
      return {
        [`${prefixe}_moy`]: Number(moy.toFixed(4)),
        [`${prefixe}_med`]: Number((moy * (0.96 + 0.02 * r(1))).toFixed(4)),
        [`${prefixe}_q75`]: Number((moy * (1.12 + 0.04 * r(2))).toFixed(4)),
        [`${prefixe}_q90`]: Number((moy * (1.26 + 0.05 * r(3))).toFixed(4)),
        [`${prefixe}_max`]: Number((moy * (1.46 + 0.08 * r(4))).toFixed(4)),
      };
    };

    const eau = [];
    const irrigation = [];

    if (elevage) {
      // Ordre de grandeur : vache en lactation ~ 90 L/j, porc ~ 12 L/j.
      const niveau = String(idProduction || "").startsWith("C") ? 12 : 90;
      horizons.forEach((Years, yi) => {
        for (let j = 0; j < 365; j += 3) {
          const base = niveau * (1 + 0.25 * Math.sin(((j - 100) / 365) * 2 * Math.PI));
          const moy =
            base * (1 + yi * 0.06) * severite +
            (rand(seedEau + j * 7 + yi * 1000) - 0.5) * niveau * 0.04;
          eau.push({
            Years,
            jour_campagne: j + 1,
            ...statistiques("besoin", Math.max(0, moy), seedEau + j * 11 + yi * 500),
            date_affichage: addDays("2025-01-01", j),
          });
        }
      });
      return Promise.resolve({ eau, irrigation });
    }

    const semisDate = semisToDate(semis);
    horizons.forEach((Years, yi) => {
      for (let j = 1; j <= 180; j += 2) {
        const dateAffichage = addDays(semisDate, j);

        const baseEau = 2.5 + 2 * Math.sin((j / 180) * Math.PI);
        const moyEau =
          baseEau * (1 + yi * 0.18) * severite +
          (rand(seedEau + j * 7 + yi * 1000) - 0.5) * 0.6;
        eau.push({
          Years,
          jour_campagne: j,
          ...statistiques("besoin", Math.max(0, moyEau), seedEau + j * 11 + yi * 500),
          date_affichage: dateAffichage,
        });

        const baseIrr = 1.2 + 1.5 * Math.sin((j / 180) * Math.PI);
        const moyIrr =
          baseIrr * (1 + yi * 0.22) * severite +
          (rand(seedIrr + j * 5 + yi * 900) - 0.5) * 0.4;
        irrigation.push({
          Years,
          jour_campagne: j,
          ...statistiques("irr", Math.max(0, moyIrr), seedIrr + j * 13 + yi * 700),
          date_affichage: dateAffichage,
        });
      }
    });
    return Promise.resolve({ eau, irrigation });
  },

  // Ancien point d'entrée TRACC : getEauIrrigation gère désormais TRACC
  // lui-même (via rcp), on garde l'alias pour api.js.
  getEauIrrigationTRACC(params = {}) {
    return mock.getEauIrrigation({ ...params, rcp: "tracc" });
  },

  getMarge({ idProdu, gridno, par_ha }) {
    const seed = seedFrom(String(idProdu || "") + String(gridno || "") + "marge");
    // Modalités telles que les renvoie l'API : conduites 1 à 5, plus les deux
    // bornes (irrigation complète et conduite sèche).
    const modalites = [
      "Mais1", "Mais2", "Mais3", "Mais4", "Mais5",
      "MaisFullIrrig", "MaisSec",
    ];
    const unite = par_ha ? 1 : 45; // simule "surface * marge/ha"
    const rows = [];
    modalites.forEach((modalite, mi) => {
      let cumul = 0;
      for (let year = 2020; year <= 2050; year++) {
        const base = (mi - 1) * 150 - (year - 2020) * 8;
        const bruit = (rand(seed + year + mi * 50) - 0.5) * 200;
        const marge_annuelle = Math.round((base + bruit) * unite) / (par_ha ? 1 : 1);
        cumul += marge_annuelle;
        rows.push({
          modalite,
          year,
          marge_annuelle,
          marge_cumulee: Math.round(cumul),
          borne_bas: Math.round(marge_annuelle * 0.8),
          borne_haut: Math.round(marge_annuelle * 1.2),
          cumul_bas: Math.round(cumul * 0.85),
          cumul_haut: Math.round(cumul * 1.15),
        });
      }
    });
    // L'API renvoie { rmo, data } et non un tableau nu : MargeTab lit
    // response.data et response.rmo.
    return Promise.resolve({ rmo: 7.6124, data: rows });
  },



/*   
████████╗███████╗██████╗░██████╗░██╗████████╗░█████╗░██╗██████╗░███████╗
╚══██╔══╝██╔════╝██╔══██╗██╔══██╗██║╚══██╔══╝██╔══██╗██║██╔══██╗██╔════╝
░░░██║░░░█████╗░░██████╔╝██████╔╝██║░░░██║░░░██║░░██║██║██████╔╝█████╗░░
░░░██║░░░██╔══╝░░██╔══██╗██╔══██╗██║░░░██║░░░██║░░██║██║██╔══██╗██╔══╝░░
░░░██║░░░███████╗██║░░██║██║░░██║██║░░░██║░░░╚█████╔╝██║██║░░██║███████╗
░░░╚═╝░░░╚══════╝╚═╝░░╚═╝╚═╝░░╚═╝╚═╝░░░╚═╝░░░░╚════╝░╚═╝╚═╝░░╚═╝╚══════╝ */



};

/**
 * Tension par élément : une fonction par élément suivi, chacune renvoyant la
 * table à plat { Years, jour_campagne, besoin_moy, besoin_sd, date_affichage
 * } — même format que la vraie route "eau". Pour brancher les vraies
 * valeurs du collègue d'Aliénor sur un élément : remplacer tout le corps de
 * la fonction correspondante par
 *   return [
 *     { Years: 2020, jour_campagne: 3, besoin_moy: 0.8975, besoin_sd: 0.655, date_affichage: "2025-04-18" },
 *     ...
 *   ];
 * (copier-coller direct de ce qu'il fournit) — rien d'autre à changer,
 * getTension() ci-dessus consomme déjà cette forme telle quelle. Si le
 * compte de démo n'a de vraies données que pour un seul scénario/année,
 * laisser les autres combinaisons sur les valeurs générées ci-dessous en
 * attendant : `rcp`/`annee` ne servent qu'à ça, à filtrer/générer le reste.
 */
function genererLignesTension({ rcp, jourPic, pic }) {
  const debut = "2025-04-15";
  // Mêmes valeurs que la sidebar envoie (useHorizon/horizonsSelectionnables),
  // sans le "+" que porte RECHAUFFEMENT plus haut dans ce fichier (une
  // convention différente, utilisée par d'autres mocks).
  const anneesGroupe = rcp === "tracc" ? ["reference", "2", "2.7"] : YEARS;
  const seed = seedFrom(`${jourPic}-${pic}-${rcp}`);

  const lignes = [];
  anneesGroupe.forEach((Years, yi) => {
    for (let jour = 0; jour <= 180; jour += 3) {
      const ecart = (jour - jourPic) / 55;
      const base = pic * facteurRcp(rcp) * (1 + yi * 0.12) * Math.exp(-ecart * ecart);
      const bruit = (rand(seed + jour * 3 + yi * 500) - 0.5) * 0.05;
      lignes.push({
        Years,
        jour_campagne: jour,
        date_affichage: addDays(debut, jour),
        besoin_moy: Number(Math.max(0, base + bruit).toFixed(4)),
        besoin_sd: Number((0.05 + rand(seed + jour) * 0.05).toFixed(4)),
      });
    }
  });
  return lignes;
}

function lignesTensionCochons(rcp) {
  return genererLignesTension({ rcp, jourPic: 110, pic: 0.70 });
}
function lignesTensionVaches(rcp) {
  return genererLignesTension({ rcp, jourPic: 110, pic: 1.10 });
}
function lignesTensionTournesol(rcp) {
  return genererLignesTension({ rcp, jourPic: 85, pic: 1.60 });
}
function lignesTensionMais(rcp) {
  return genererLignesTension({ rcp, jourPic: 95, pic: 2.40 });
}
